import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const scrollLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const SliderWrapper = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  borderTop: '1px solid rgba(0,0,0,0.06)',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',

  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '120px',
    zIndex: 2,
    pointerEvents: 'none',
  },
  '&::before': {
    left: 0,
    background: 'linear-gradient(to right, #ffffff 0%, transparent 100%)',
  },
  '&::after': {
    right: 0,
    background: 'linear-gradient(to left, #ffffff 0%, transparent 100%)',
  },
}));

const Track = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: 'max-content',
  animation: `${scrollLeft} 45s linear infinite`,

  '&:hover': {
    animationPlayState: 'paused',
  },
});

const BrandItem = styled('a', {
  shouldForwardProp: (prop) => prop !== 'plated',
})(({ theme, plated }) => ({
  // Equal-width slots keep every mark optically centred, whatever its aspect ratio.
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: '208px',
  height: '56px',
  padding: theme.spacing(0, 3),
  flexShrink: 0,
  opacity: plated ? 0.92 : 0.75,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  textDecoration: 'none',
  cursor: 'pointer',

  [theme.breakpoints.down('sm')]: {
    width: '156px',
    padding: theme.spacing(0, 2),
  },

  '&:hover': {
    opacity: 1,
    transform: 'scale(1.08)',
  },

  '& img': {
    height: '44px',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
  },
}));

// Light-on-transparent logos need their own backing plate to stay legible on the white rail.
const BrandPlate = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'plateColor',
})(({ theme, plateColor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: plateColor,
  borderRadius: '10px',
  padding: theme.spacing(1, 2),
  maxWidth: '100%',

  '& img': {
    height: '26px',
  },
}));

const brands = [
  // Products
  { name: 'DeenMatches', image: '/images/products/deenmatches-icon.png', url: 'https://deenmatches.co.uk' },
  { name: 'UBC Connect', image: '/images/products/ubc-connect-icon.png', url: 'https://ubc-connect.co.uk' },
  { name: 'Suvai Foods UK', image: '/images/products/suvai-foods-icon.png', url: 'https://suvaifoods.co.uk/' },
  { name: 'Watford Mosque Association', image: '/images/products/watford-mosque-icon.png', url: 'https://watfordmosque.nexfah.com' },
  { name: 'BookMotEasy', image: '/images/products/bookmoteasy-icon.png', url: 'https://bookmoteasy.co.uk' },
  { name: 'Crux360 AI', image: '/images/products/crux360-icon.png', url: 'https://crux360.ai' },
  // Clients
  { name: 'Vision Bank', image: '/images/brands/vision-bank.svg', plateColor: '#0A1628' },
  { name: 'Al Rajhi Bank', image: '/images/brands/alrajhi-bank.png' },
  { name: 'Arab National Bank', image: '/images/brands/anb.png' },
  { name: 'Visible Stars', image: '/images/brands/visible-stars.png' },
  { name: 'UK Tamil Muslims', image: '/images/brands/uktm.png' },
  { name: 'Tekcode', image: '/images/brands/tekcode.png' },
];

export default function BrandSlider() {
  // Duplicate items to create seamless infinite loop
  const items = [...brands, ...brands];

  return (
    <SliderWrapper>
      <Track>
        {items.map((brand, index) => (
          <BrandItem
            key={index}
            plated={brand.plateColor ? 1 : 0}
            href={brand.url || undefined}
            target={brand.url ? '_blank' : undefined}
            rel={brand.url ? 'noopener noreferrer' : undefined}
            title={brand.name}
            style={{ cursor: brand.url ? 'pointer' : 'default' }}
          >
            {brand.plateColor ? (
              <BrandPlate plateColor={brand.plateColor}>
                <img src={brand.image} alt={brand.name} />
              </BrandPlate>
            ) : (
              <img src={brand.image} alt={brand.name} />
            )}
          </BrandItem>
        ))}
      </Track>
    </SliderWrapper>
  );
}
